/**
 * Maps **Next.js app locale strings** (`en-se`, `de`, …) to parameters required by the
 * Centra Checkout API in static mode (market and pricelist). Centra remains the basket
 * backend only — locale ownership stays in `NEXT_APP_CONFIG` / `APPS`.
 */
import { type CentraPricelistId } from "@/data/centra-currency-id"
import { SHIPPING_COUNTRY_CODES } from "@/data/shipping-country-code"
import {
    CENTRA_PRICELIST_CODE,
    type CentraPricelistCode
} from "@/data/supported-pricelists"
import { CENTRA_MARKET_CODE, type CentraMarketCode } from "@/data/supported-markets"
import centraPricelistCodeToCentraPricelistId from "@/i18n/maps/country-code-to-currency"
import marketCodeToMarketIdMap, {
    type MarketId
} from "@/i18n/maps/market-code-to-market-id"
import { mapMarketCodeToMarketId } from "@/i18n/maps/map-utils"
import { isStringEuroCurrencyPreferredApp } from "@/src/utils/strings/isStringEuroCurrencyPreferredApp"

/** Second segment of a locale path (`en-se` → `se`; language-only `de` → `""`). */
export type LocalePathCountrySegment = string

const segmentToCentraMarketCode = new Map<string, CentraMarketCode>([
    ["", CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.CzechRepublic, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Denmark, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Norway, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Poland, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Sweden, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Switzerland, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.UnitedKingdom, CENTRA_MARKET_CODE.Eu],
    [SHIPPING_COUNTRY_CODES.Canada, CENTRA_MARKET_CODE.Na],
    [SHIPPING_COUNTRY_CODES.USA, CENTRA_MARKET_CODE.Na],
    [SHIPPING_COUNTRY_CODES.Australia, CENTRA_MARKET_CODE.Apac],
    [SHIPPING_COUNTRY_CODES.NewZealand, CENTRA_MARKET_CODE.Apac],
    [SHIPPING_COUNTRY_CODES.Japan, CENTRA_MARKET_CODE.Jp]
])

const segmentToCentraPricelistCode = new Map<string, CentraPricelistCode>([
    [SHIPPING_COUNTRY_CODES.Australia, CENTRA_PRICELIST_CODE.aud],
    [SHIPPING_COUNTRY_CODES.Canada, CENTRA_PRICELIST_CODE.cad],
    [SHIPPING_COUNTRY_CODES.Switzerland, CENTRA_PRICELIST_CODE.chf],
    [SHIPPING_COUNTRY_CODES.CzechRepublic, CENTRA_PRICELIST_CODE.czk],
    [SHIPPING_COUNTRY_CODES.Denmark, CENTRA_PRICELIST_CODE.dkk],
    [SHIPPING_COUNTRY_CODES.UnitedKingdom, CENTRA_PRICELIST_CODE.gbp],
    [SHIPPING_COUNTRY_CODES.Japan, CENTRA_PRICELIST_CODE.jpy],
    [SHIPPING_COUNTRY_CODES.Norway, CENTRA_PRICELIST_CODE.nok],
    [SHIPPING_COUNTRY_CODES.NewZealand, CENTRA_PRICELIST_CODE.nzd],
    [SHIPPING_COUNTRY_CODES.Poland, CENTRA_PRICELIST_CODE.pln],
    [SHIPPING_COUNTRY_CODES.Sweden, CENTRA_PRICELIST_CODE.sek],
    [SHIPPING_COUNTRY_CODES.USA, CENTRA_PRICELIST_CODE.usd]
])

export const extractCountrySegmentFromAppLocale = (appLocale: string): string => {
    const normalized = appLocale.replace(/^\//, "").toLowerCase()
    const dash = normalized.indexOf("-")
    if (dash === -1) return ""
    return normalized.slice(dash + 1)
}

export const getMarketCodeFromAppLocale = (appLocale: string): CentraMarketCode => {
    const normalized = appLocale.replace(/^\//, "").toLowerCase()
    if (isStringEuroCurrencyPreferredApp(normalized)) {
        return CENTRA_MARKET_CODE.Eu
    }
    const segment = extractCountrySegmentFromAppLocale(normalized)
    return segmentToCentraMarketCode.get(segment) ?? CENTRA_MARKET_CODE.Eu
}

export const mapPreferredAppToPriceListCode = (
    preferredApp: string
): CentraPricelistCode | undefined => {
    const normalized = preferredApp.replace(/^\//, "").toLowerCase()
    if (isStringEuroCurrencyPreferredApp(normalized)) {
        return CENTRA_PRICELIST_CODE.eur
    }
    const segment = extractCountrySegmentFromAppLocale(normalized)
    if (segment === "") return CENTRA_PRICELIST_CODE.eur

    // Symmetric with the market's `?? Eu` fallback: a country segment with no explicit
    // non-EUR currency falls back to EUR. Covers eurozone countries reached via a
    // country-suffixed locale (e.g. `de-de`, `fr-fr`) that previously only existed as a
    // language-only path. Without this the segment resolves to `undefined` and `apps.ts`
    // throws at module load.
    return segmentToCentraPricelistCode.get(segment) ?? CENTRA_PRICELIST_CODE.eur
}

export const mapPreferredAppToPriceListId = (
    preferredApp: string
): CentraPricelistId | undefined => {
    const code = mapPreferredAppToPriceListCode(preferredApp)
    if (!code) return undefined
    return centraPricelistCodeToCentraPricelistId.get(code)
}

export const mapPreferredAppToMarketCodeThenToMarketId = (
    preferredApp: string
): MarketId => {
    const marketCode = getMarketCodeFromAppLocale(preferredApp)
    const marketId = mapMarketCodeToMarketId(marketCode)
    return marketId || (marketCodeToMarketIdMap.get(CENTRA_MARKET_CODE.Eu) as MarketId)
}
